/**
 * Application store.
 *
 * Global, cross-cutting runtime and UI state that isn't specific to one
 * feature: shell readiness, backend status, the sidebar collapse flag, the
 * command-palette toggle, and the transient toast queue.
 */
import { create } from "zustand";
import type { BackendStatus, Toast } from "@/types";
import { createId } from "@/utils";

interface AppState {
  /** True once the shell has finished its startup sequence. */
  ready: boolean;
  /** Readiness of the (future) local AI backend. */
  backendStatus: BackendStatus;
  /** Whether the sidebar is collapsed to its icon rail. */
  sidebarCollapsed: boolean;
  /** Whether the command palette / search overlay is open. */
  commandPaletteOpen: boolean;
  /** Active toast notifications. */
  toasts: Toast[];
  /** Text queued to prefill the chat composer on its next mount/update —
   * the single bridge "insert prompt," "send clipboard to AI," "chat about
   * this note," and Quick Actions' "insert into chat" all write through,
   * consumed once by `InputBar`. */
  pendingComposerText: string | null;

  setReady: (ready: boolean) => void;
  setBackendStatus: (status: BackendStatus) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setPendingComposerText: (text: string | null) => void;

  addToast: (toast: Omit<Toast, "id" | "duration"> & { duration?: number }) => string;
  dismissToast: (id: string) => void;
}

export const useAppStore = create<AppState>()((set) => ({
  ready: false,
  backendStatus: "offline",
  sidebarCollapsed: false,
  commandPaletteOpen: false,
  toasts: [],
  pendingComposerText: null,

  setReady: (ready) => set({ ready }),
  setBackendStatus: (backendStatus) => set({ backendStatus }),
  toggleSidebar: () =>
    set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
  setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
  setPendingComposerText: (pendingComposerText) => set({ pendingComposerText }),

  addToast: (toast) => {
    const id = createId("toast");
    const full: Toast = {
      id,
      title: toast.title,
      description: toast.description,
      variant: toast.variant,
      duration: toast.duration ?? 4000,
    };
    set((s) => ({ toasts: [...s.toasts, full] }));
    return id;
  },
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
